import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { 
  Phone, CheckCircle, ArrowRight, Star, Clock, Shield, Award, 
  ChevronDown, ChevronUp, MapPin, AlertTriangle, Calendar,
  Droplets, Flame, ShowerHead, Thermometer, Wrench, Home,
  Construction, Search, Plug, RefreshCw, ChefHat, Bath, Droplet,
  GitBranch, Waves, Video, Database, Zap, AlertCircle, Settings,
  TrendingUp, Layers, Maximize, Target, Trees, ArrowDownCircle,
  Wind, Fan, Smile, ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import { ServiceSchema, BreadcrumbSchema, FAQSchema, GeoTargetingSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { services, testimonials, businessInfo } from '../data/mock';
import { serviceContent } from '../data/serviceContent';

// Icon mapping
const iconMap = {
  Droplets, Flame, ShowerHead, Thermometer, Wrench, Home, Construction,
  Search, Plug, RefreshCw, AlertTriangle, ChefHat, Bath, Droplet,
  GitBranch, Waves, Video, Database, Zap, AlertCircle, Settings,
  TrendingUp, Layers, Maximize, Target, Trees, ArrowDownCircle,
  Wind, Fan, Smile, ArrowUpRight, CheckCircle
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  const content = serviceContent[slug];
  const [openFaq, setOpenFaq] = useState(null);
  
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  if (!service) {
    return (
      <>
        <ScrollToTop />
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-[#1a3a5c] mb-4">Service Not Found</h1>
            <p className="text-gray-600 mb-8">The service you're looking for doesn't exist.</p>
            <Link to="/services">
              <Button>View All Services</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Services', url: 'https://basinplumbing.com/services/' },
    { name: service.title, url: `https://basinplumbing.com/services/${service.slug}/` }
  ];

  const relatedServices = services.filter(s => s.id !== service.id).slice(0, 4);
  const faqs = content?.faqs || [];

  // Render different section types
  const renderSection = (section, index) => {
    switch (section.type) {
      case 'intro':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-6">{section.h2}</h2>
            <div className="prose prose-lg max-w-none">
              {section.content.split('\n\n').map((para, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-4">{para}</p>
              ))}
            </div>
          </div>
        );

      case 'signs':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-8">{section.h2}</h2>
            <div className="space-y-4">
              {section.items.map((item, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">{item.title}</h3>
                  <p className="text-gray-600 mb-3">{item.content}</p>
                  {item.link && (
                    <Link to={item.link.url} className="text-[#e85a24] font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                      {item.link.text}
                      <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'services-grid':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-8">{section.h2}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, i) => {
                const IconComponent = iconMap[item.icon] || Wrench;
                return (
                  <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-all group">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-[#1a3a5c]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#e85a24] transition-colors">
                        <IconComponent className="text-[#1a3a5c] group-hover:text-white transition-colors" size={24} />
                      </div>
                      <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3">{item.content}</p>
                      {item.best && (
                        <p className="text-xs text-[#e85a24] font-medium">{item.best}</p>
                      )}
                      {item.link && (
                        <Link to={item.link.url} className="text-[#e85a24] font-medium text-sm inline-flex items-center gap-1 mt-2 hover:gap-2 transition-all">
                          {item.link.text}
                          <ArrowRight size={14} />
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );

      case 'process':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-8">{section.h2}</h2>
            <div className="space-y-6">
              {section.steps.map((step, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="w-16 h-16 bg-[#e85a24] rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">{step.number}</span>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'comparison':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4">{section.h2}</h2>
            {section.content && <p className="text-gray-600 mb-8">{section.content}</p>}
            <div className="grid md:grid-cols-2 gap-6">
              {section.options.map((option, i) => (
                <Card key={i} className={`border-2 ${option.negative ? 'border-gray-200 bg-gray-50' : 'border-[#1a3a5c] bg-white'}`}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#1a3a5c] mb-2">{option.title}</h3>
                    {option.best && (
                      <p className={`text-sm mb-4 ${option.negative ? 'text-gray-500' : 'text-[#e85a24] font-medium'}`}>
                        {option.best}
                      </p>
                    )}
                    <ul className="space-y-2">
                      {option.features.map((feature, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckCircle size={16} className={`mt-1 flex-shrink-0 ${option.negative ? 'text-gray-400' : 'text-green-500'}`} />
                          <span className={option.negative ? 'text-gray-500' : 'text-gray-700'}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {option.link && (
                      <Link to={option.link.url}>
                        <Button className="w-full mt-6 bg-[#1a3a5c] hover:bg-[#2a5a8c]">
                          {option.link.text}
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            {section.link && (
              <div className="text-center mt-6">
                <Link to={section.link.url} className="text-[#e85a24] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                  {section.link.text}
                  <ArrowRight size={18} />
                </Link>
              </div>
            )}
          </div>
        );

      case 'pricing':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4">{section.h2}</h2>
            {section.content && <p className="text-gray-600 mb-6">{section.content}</p>}
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardContent className="p-0">
                <div className="divide-y divide-gray-100">
                  {section.priceItems.map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-4 hover:bg-gray-50 transition-colors">
                      <span className="font-medium text-[#1a3a5c]">{item.service}</span>
                      <span className="text-[#e85a24] font-semibold">{item.range}</span>
                    </div>
                  ))}
                </div>
                {section.note && (
                  <div className="p-4 bg-gray-50 border-t">
                    <p className="text-sm text-gray-600">{section.note}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            {section.link && (
              <div className="text-center mt-6">
                <Link to={section.link.url}>
                  <Button className="bg-[#e85a24] hover:bg-[#d14a14]">
                    {section.link.text}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        );

      case 'warning':
        return (
          <div key={index} className="mb-16">
            <Card className="border-2 border-red-200 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="text-white" size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-red-800 mb-2">{section.h2}</h2>
                    <p className="text-red-700 mb-4">{section.content}</p>
                    <ul className="space-y-2 mb-4">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-red-700">
                          <AlertTriangle size={14} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-red-800 font-medium">{section.action}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'cta-mid':
        return (
          <div key={index} className="mb-16">
            <Card className="border-0 bg-gradient-to-r from-[#1a3a5c] to-[#2a5a8c] text-white">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">{section.h2}</h2>
                <p className="text-blue-100 mb-6">{section.content}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {section.buttons.map((btn, i) => (
                    btn.primary ? (
                      <Link key={i} to={btn.url}>
                        <Button className="bg-[#e85a24] hover:bg-[#d14a14] text-white px-8">
                          {btn.text}
                        </Button>
                      </Link>
                    ) : (
                      <a key={i} href={btn.url}>
                        <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1a3a5c] px-8">
                          {btn.text}
                        </Button>
                      </a>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'local':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-6">{section.h2}</h2>
            <div className="prose prose-lg max-w-none mb-6">
              {section.content.split('\n\n').map((para, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-4">{para}</p>
              ))}
            </div>
            {section.areas && (
              <div className="flex flex-wrap gap-2 mb-4">
                {section.areas.map((area, i) => (
                  <span key={i} className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-[#1a3a5c] hover:bg-[#1a3a5c] hover:text-white transition-colors cursor-default">
                    {area}, TX
                  </span>
                ))}
              </div>
            )}
            {section.link && (
              <Link to={section.link.url} className="text-[#e85a24] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                {section.link.text}
                <ArrowRight size={18} />
              </Link>
            )}
          </div>
        );

      case 'local-challenges':
        return (
          <div key={index} className="mb-16 bg-[#1a3a5c] text-white rounded-2xl p-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{section.h2}</h2>
            <p className="text-blue-100 mb-8">{section.content}</p>
            <div className="grid md:grid-cols-2 gap-6">
              {section.items.map((item, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-[#e85a24]" />
                    {item.title}
                  </h3>
                  <p className="text-blue-100 text-sm leading-relaxed">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'local-service-areas':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4">{section.h2}</h2>
            <p className="text-gray-600 mb-8">{section.content}</p>
            <div className="grid md:grid-cols-3 gap-6">
              {section.areas.map((area, i) => (
                <Card key={i} className="border-0 shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <div className="bg-[#1a3a5c] text-white p-4">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <MapPin size={18} />
                        {area.city}
                      </h3>
                      <p className="text-blue-200 text-sm flex items-center gap-1 mt-1">
                        <Clock size={14} />
                        {area.response} response
                      </p>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {area.neighborhoods.map((hood, j) => (
                          <span key={j} className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-700">
                            {hood}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'maintenance':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4">{section.h2}</h2>
            <p className="text-gray-600 mb-6">{section.content}</p>
            <div className="grid md:grid-cols-3 gap-6">
              {section.tips.map((tip, i) => (
                <Card key={i} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#1a3a5c] mb-4">{tip.title}</h3>
                    <ul className="space-y-2">
                      {tip.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-gray-600 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
            {section.link && (
              <div className="text-center mt-6">
                <Link to={section.link.url} className="text-[#e85a24] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                  {section.link.text}
                  <ArrowRight size={18} />
                </Link>
              </div>
            )}
          </div>
        );

      case 'candor':
        return (
          <div key={index} className="mb-16">
            <Card className="border-2 border-[#1a3a5c]/20 bg-[#1a3a5c]/5">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-[#1a3a5c] mb-4">{section.h2}</h2>
                <div className="prose prose-lg max-w-none">
                  {section.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-gray-700 leading-relaxed mb-4 whitespace-pre-line">{para}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'why-us':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-8">{section.h2}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, i) => (
                <div key={i} className="p-6 bg-gray-50 rounded-xl">
                  <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'emergency':
        return (
          <div key={index} className="mb-16">
            <Card className="border-0 bg-[#e85a24] text-white">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">{section.h2}</h2>
                <div className="prose prose-invert mb-6">
                  {section.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-white/90 whitespace-pre-line">{para}</p>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  {section.buttons.map((btn, i) => (
                    btn.primary ? (
                      <a key={i} href={btn.url}>
                        <Button className="bg-white text-[#e85a24] hover:bg-gray-100 px-8">
                          <Phone className="mr-2" size={18} />
                          {btn.text}
                        </Button>
                      </a>
                    ) : (
                      <Link key={i} to={btn.url}>
                        <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#e85a24] px-8">
                          <Calendar className="mr-2" size={18} />
                          {btn.text}
                        </Button>
                      </Link>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'facilities':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-8">{section.h2}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {section.categories.map((cat, i) => (
                <Card key={i} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#1a3a5c] mb-4">{cat.title}</h3>
                    <ul className="space-y-2">
                      {cat.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-2 text-gray-600">
                          <CheckCircle size={14} className="text-[#e85a24]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'compliance':
        return (
          <div key={index} className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-6">{section.h2}</h2>
            <Card className="border-2 border-[#1a3a5c]/20">
              <CardContent className="p-6">
                <div className="prose prose-lg max-w-none">
                  {section.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-gray-700 leading-relaxed mb-4 whitespace-pre-line">{para}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ScrollToTop />
      <Seo
        title={content?.metaTitle || `${service.title} in Odessa & Midland, TX | Basin Plumbing`}
        description={content?.metaDescription || service.shortDesc}
        path={`/services/${service.slug}`}
        keywords={service.keywords?.join(', ')}
        image={service.image}
      />
      <ServiceSchema service={service} />
      <BreadcrumbSchema items={breadcrumbs} />
      {faqs.length > 0 && <FAQSchema customFaqs={faqs} />}
      <GeoTargetingSchema city="Odessa" />
      
      <Header />
      
      <main className="main-content">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
          
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="text-sm text-blue-200 mb-6" aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><span className="mx-1">/</span></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><span className="mx-1">/</span></li>
                <li><span className="text-white">{service.title}</span></li>
              </ol>
            </nav>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-[#e85a24] font-semibold">{service.title} Experts</span>
            </div>
            
            {/* H1 */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 max-w-4xl">
              {content?.h1 || `${service.title} in Odessa & Midland`}
            </h1>
            
            <p className="text-xl text-blue-100 mb-8 max-w-3xl leading-relaxed">
              {content?.heroSubtitle || service.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/contact">
                <Button size="lg" className="bg-[#e85a24] hover:bg-[#d14a14] text-white px-8 py-6 text-lg">
                  Get Free Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1a3a5c] px-8 py-6 text-lg">
                  <Calendar className="mr-2" size={20} />
                  Schedule Service
                </Button>
              </Link>
            </div>

            {/* Quick Services Pills */}
            {content?.quickServices && (
              <div className="mb-8">
                <h3 className="text-sm text-blue-200 mb-3 uppercase tracking-wider">What We Do</h3>
                <div className="flex flex-wrap gap-2">
                  {content.quickServices.map((item, i) => (
                    <span key={i} className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg text-white text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Stats Section */}
        {content?.stats && (
          <section className="py-12 bg-white border-b">
            <div className="max-w-7xl mx-auto px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {content.stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-3xl md:text-4xl font-bold text-[#e85a24] mb-1">{stat.value}</p>
                    <p className="text-gray-600 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Main Content */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            {content?.sections?.map((section, index) => renderSection(section, index))}
          </div>
        </section>

        {/* FAQ Section */}
        {faqs.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4 text-center">
                {service.title} FAQs
              </h2>
              <p className="text-gray-600 text-center mb-10">
                Common questions about our {service.title.toLowerCase()} services
              </p>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-semibold text-[#1a3a5c] pr-4">{faq.question}</span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        openFaq === index ? 'bg-[#e85a24]' : 'bg-gray-200'
                      }`}>
                        {openFaq === index ? (
                          <ChevronUp className="text-white" size={20} />
                        ) : (
                          <ChevronDown className="text-gray-500" size={20} />
                        )}
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}>
                      <div className="px-6 pb-5">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Services */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-[#1a3a5c] mb-8 text-center">Related Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedServices.map((relatedService) => (
                <Link key={relatedService.id} to={`/services/${relatedService.slug}`}>
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 group">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2 group-hover:text-[#e85a24] transition-colors">
                        {relatedService.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {relatedService.shortDesc}
                      </p>
                      <span className="text-[#e85a24] font-medium text-sm flex items-center gap-1">
                        Learn More
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title={`Ready for Professional ${service.title}?`}
          subtitle={`Schedule your free estimate today. Serving Odessa, Midland, and the entire Permian Basin.`}
        />
      </main>

      <Footer />
    </>
  );
};

export default ServiceDetailPage;
